import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ContentViewComponent } from '@principle-theorem/ng-interactions';
import {
  ButtonsContainerComponent,
  NgCommonModule,
  SelectionListStore,
} from '@principle-theorem/ng-shared';
import { IClinicalNote } from '@principle-theorem/principle-core/interfaces';
import {
  HISTORY_DATE_FORMAT,
  isSameIdentifier,
  isSameRef,
  WithRef,
} from '@principle-theorem/shared';
import { MomentModule } from 'ngx-moment';
import { Observable } from 'rxjs';

export interface IClinicalNoteSelectionDialogData {
  clinicalNotes: WithRef<IClinicalNote>[];
  selectedClinicalNotes?: IClinicalNote[];
}

@Component({
  selector: 'pr-clinical-note-selection-dialog',
  imports: [
    NgCommonModule,
    MatToolbarModule,
    MatDialogModule,
    ButtonsContainerComponent,
    MatButtonModule,
    MatTableModule,
    MatIconModule,
    MatCheckboxModule,
    ContentViewComponent,
    MomentModule,
    MatTooltipModule,
  ],
  providers: [SelectionListStore],
  templateUrl: './clinical-note-selection-dialog.component.html',
  styleUrl: './clinical-note-selection-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicalNoteSelectionDialogComponent {
  selectionList = inject(SelectionListStore<WithRef<IClinicalNote>>);
  dialogRef = inject(MatDialogRef<ClinicalNoteSelectionDialogComponent>);
  readonly data = inject<IClinicalNoteSelectionDialogData>(MAT_DIALOG_DATA);
  readonly dateFormat = HISTORY_DATE_FORMAT;
  readonly columnsToDisplay = ['recordDate', 'recordedBy', 'expand', 'select'];

  dataSource = signal<WithRef<IClinicalNote>[]>([]);
  expandedRecordIds = signal<Set<string>>(new Set<string>());
  allExpanded = signal<boolean>(false);
  selected = toSignal(this.selectionList.selected$, { initialValue: [] });

  expandedStateMap = computed(() => {
    const expandedIds = this.expandedRecordIds();
    const result = new Map<string, boolean>();

    this.dataSource().forEach((record) => {
      result.set(record.uid, expandedIds.has(record.uid));
    });

    return result;
  });

  isRecordExpanded = computed(() => {
    const expandedIds = this.expandedRecordIds();
    return (record: WithRef<IClinicalNote>) => expandedIds.has(record.uid);
  });

  hasChanges = computed(() => {
    const initialSelection = this.data.selectedClinicalNotes || [];
    const currentSelection = this.selected();

    if (initialSelection.length !== currentSelection.length) {
      return true;
    }

    return !initialSelection.every((initialRef) =>
      currentSelection.some((selected) =>
        isSameIdentifier(initialRef, selected)
      )
    );
  });

  constructor() {
    this.dataSource.set(this.data.clinicalNotes);
    this.selectionList.setCompareFn(isSameRef);
    this.selectionList.loadOptions(this.data.clinicalNotes);
    this._setSelected(this.data);
  }

  isSelected$(note: WithRef<IClinicalNote>): Observable<boolean> {
    return this.selectionList.isSelected$(note);
  }

  toggle(element: WithRef<IClinicalNote>): void {
    this.expandedRecordIds.update((ids) => {
      const newIds = new Set(ids);
      if (newIds.has(element.uid)) {
        newIds.delete(element.uid);
        return newIds;
      }

      newIds.add(element.uid);
      return newIds;
    });
  }

  toggleAllExpanded(): void {
    const newState = !this.allExpanded();
    this.allExpanded.set(newState);

    if (newState) {
      const allIds = new Set(this.dataSource().map((record) => record.uid));
      this.expandedRecordIds.set(allIds);
      return;
    }
    this.expandedRecordIds.set(new Set());
  }

  private _setSelected(data: IClinicalNoteSelectionDialogData): void {
    const selectedRefs = data.selectedClinicalNotes || [];
    const selectedNotes = data.clinicalNotes.filter((note) =>
      selectedRefs.some((ref) => isSameIdentifier(ref, note))
    );

    selectedNotes.forEach((note) => {
      this.selectionList.setSelected(note, true);
    });
  }
}
